//
import Model from '@/models/Model'
import DetailPurchaseOrderModel from '@/models/Purchase/PchDetailPurchaseOrder'
import CloseModel from '@/models/Actions/AClose'
import ClosePartiallyModel from '@/models/Actions/AClosePartially'

/**
 * @description
 * This class describes a pch purchase order.
 *
 * @class      PchPurchaseOrder (name)
 */
export default class PchPurchaseOrder extends Model {

    /**
     * @description
     * The default pch purchase order includes
     */
    static includes = [
        'g_authorizer_user.hr_employee',
        'g_originator_user.hr_employee',
        'pch_detail_purchase_orders.wh_product',
        'pch_detail_purchase_orders.wh_product_service',
        'pch_payment_condition',
        'pch_provider',
        'pch_provider_branch_offices',
        'pch_purchase_order_state',
        'pch_purchase_order_has_invoice_numbers.pch_purchase_invoice',
        'wh_orderers.g_supervisor_user',
        'wh_orderers.pch_provider',
        'wh_orderers.pch_provider_branch_offices',
        'wh_warehouse.g_branch_office.g_commune',
    ]

    /**
     * @description
     * The close pch purchase order endpoint
     *
     * @return     {object}  object instance
     */
    close() {
        return this.hasMany(CloseModel)
    }

    /**
     * @description
     * The close partially pch purchase order endpoint
     *
     * @return     {object}  object instance
     */
    close_partially() {
        return this.hasMany(ClosePartiallyModel)
    }

    /**
     * @description
     * The can reception pch purchase order flag
     *
     * @type       {boolean}
     */
    get can_reception() {
        return (
            this.pch_purchase_order_state_id > 3 &&
            this.pch_purchase_order_state_id < 6
        )
    }

    /**
     * @description
     * The cannot reception pch purchase order flag
     *
     * @type       {boolean}
     */
    get cannot_reception() {
        return !this.can_reception
    }

    /**
     * @description
     * The originator user name case
     *
     * @type       {string}
     */
    get g_originator_user_full_name() {
        return this.getProperty('g_originator_user.hr_employee.name_case', '-')
    }

    /**
     * @description
     * The has exceded detail reception flag
     *
     * @type       {boolean}
     */
    get has_exceded_reception() {
        return this.pch_detail_purchase_orders.filter(this.filterDetailByReceive).some(this.filterDetailByExceded)
    }

    /**
     * @description
     * The pch purchase invoice numbers
     *
     * @type       {array}
     */
    get invoice_numbers() {
        return this.pch_purchase_order_has_invoice_numbers.map(this.mapInvoiceNumberToNumber)
    }

    /**
     * @description
     * The pch purchase invoice numbers mapped to close
     *
     * @type       {array}
     */
    get invoice_numbers_to_close() {
        return this.invoice_numbers.map(this.mapInvoiceNumberToClose)
    }

    /**
     * @description
     * The pch detail purchase order mapped to close
     *
     * @type       {array}
     */
    get pch_detail_purchase_orders_to_close() {
        return this.pch_detail_purchase_orders.filter(this.filterDetailByReceive).map(this.mapDetailToClose)
    }

    /**
     * @description
     * The pch provider alias name
     *
     * @type       {string}
     */
    get pch_provider_alias_name() {
        return this.pch_provider.alias_name
    }

    /**
     * @description
     * The pch provider branch office address
     *
     * @type       {string}
     */
    get pch_provider_branch_offices_address() {
        return this.pch_provider_branch_offices.address
    }

    /**
     * @description
     * The pch purchase order state
     *
     * @type       {string}
     */
    get pch_purchase_order_state_name() {
        return this.pch_purchase_order_state.state
    }

    /**
     * @description
     * The wh orderer length
     *
     * @type       {string}
     */
    get wh_orderers_length() {
        return this.wh_orderers.length
    }

    /**
     * @description
     * The wh warehouse name
     *
     * @type       {string}
     */
    get wh_warehouse_name() {
        return this.wh_warehouse.name
    }

    /**
     * @description
     * Set the pch purchase invoice numbers
     *
     * @type       {array}
     */
    set invoice_numbers(items) {
        this.pch_purchase_order_has_invoice_numbers = items.map(this.mapHasInvoiceNumber)
    }

    /**
     * @description
     * Check if item has exceded receive
     *
     * @param      {object}  item    The item
     * @return     {boolean}  is exceded flag
     */
    filterDetailByExceded = item => {

        //
        const receive  = Number(item.receive)
        const received = Number(item.received)
        const quantity = Number(item.quantity)

        //
        return (receive + received) > quantity
    }

    /**
     * @description
     * Check if item has receive above zero
     *
     * @param      {object}   item    The item
     * @return     {boolean}  is above zero flag
     */
    filterDetailByReceive = item => {

        //
        const receive = Number(item.receive)

        //
        return receive > 0
    }

    /**
     * @description
     * Check if item has the equal invoice number of parent
     *
     * @param      {object}     value   The parent invoice_number value
     * @param      {object}     item    The item
     * @return     {boolean}    the equal flag
     */
    filterByInvoiceNumber = value => item => {
        return item.invoice_number === value
    }

    /**
     * @description
     * Return the object close representation of pch purchase order detail
     *
     * @param      {object}  item    The item
     * @return     {Object}  The detail close object data
     */
    mapDetailToClose = item => {
        return {
            wh_product_id: Number(item.wh_product_id),
            wh_product_service_id: Number(item.wh_product_service_id),
            received: Number(item.receive)            
        }
    }

    /**
     * @description
     * Return the model representation of pch purchase order detail
     *
     * @param      {object}                    item    The detail data
     * @return     {DetailPurchaseOrderModel}  The detail purchase order model.
     */
    mapDetailToModel = item => {
        return new DetailPurchaseOrderModel({
            ...item,
            receive: 0,
        })
    }

    /**
     * @description
     * Return the invoice number object
     *
     * @param      {object}  item    The item
     * @return     {object}  the invoice number detail
     */
    mapHasInvoiceNumber = item => {

        //
        const detail = this.pch_purchase_order_has_invoice_numbers.find(this.filterByInvoiceNumber(item))

        //
        return detail || { invoice_number: item }
    }

    /**
     * @description
     * Return the Number cast invoice number representation
     *
     * @param      {any}  item    The item
     * @return     {number}  the pch purchase invoice number
     */
    mapInvoiceNumberToClose = item => {
        return Number(item)
    }

    /**
     * @description
     * Return the invoice number of include pch purchase invoices
     *
     * @param      {object}  item    The item
     * @return     {number}  The invoice-number
     */
    mapInvoiceNumberToNumber = item => {
        return item.invoice_number
    }

    /**
     * @description
     * Resets the object.
     *
     * @return     {object}  The reset pch purchaase order values
     */
    reset() {

        //
        this.pch_detail_purchase_orders = this.pch_detail_purchase_orders.map(this.mapDetailToModel)

        //
        return this
    }

    /**
     * @description
     * The pch purchase order enpoint
     *
     * @return     {string}  The endpoint value
     */
    resource() {
        return 'pch_purchase_order'
    }

    /**
     * @description
     * Returns a closing data representation of the object.
     *
     * @return     {object}  Closing data representation of the object.
     */
    toClosingData() {
        return {
            details: this.pch_detail_purchase_orders_to_close,
            invoice_numbers: this.invoice_numbers_to_close,
            reception_comment: this.reception_comment,
            wh_warehouse_reception_id: this.wh_warehouse_reception_id,
        }
    }
}
